import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { 
    FaUser, FaClipboardCheck, FaUsers, FaCog, FaCalendarAlt, 
    FaFileAlt, FaBullhorn, FaGraduationCap, FaHospital, FaKey 
} from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig.js';
import { formatMongoDate } from '../utils/dateUtils.js';
import { normalizeUser } from '../utils/userUtils';
import { isImpersonating, restoreAdminSession } from '../utils/auth';
import Navbar from "./navbar.jsx";
import "../styles/theme.css";
import CreateUserForm from "./CreateUserFormModern.jsx";
import ViewAdminSchedules from "./ViewAdminSchedules.jsx";
import ScheduleByStudentPage from "./schedules/ScheduleByStudentPage";
import ScheduleByPreceptorPage from "./schedules/ScheduleByPreceptorPage";
import ScheduleBySpecialityPage from "./schedules/ScheduleBySpecialityPage";
import ScheduleByInstitutionPage from "./schedules/ScheduleByInstitutionPage";
import ScheduleByRotationPage from "./schedules/ScheduleByRotationPage";
import ScheduleReportingPage from "./ScheduleReportingPage.jsx";
import PreceptorEvaluationForm from "./PreceptorEvaluationForm.jsx";
import AdminEvaluationCenter from "./AdminEvaluationCenter.jsx";
import ConfigurationForm from "./ConfigurationFormModern.jsx";
import ConstraintsForm from "./ConstraintsFormModern.jsx";
import ManageUsers from './ManageUsers.jsx';
import ModifyUser from "./ModifyUser.jsx";
import CollapsibleNavbar from './CollapsibleNavbar.jsx';
import ImportUsers from './ImportUsers.jsx';
import DueEvents from './DueEvents.jsx';

import ViewPreceptorSchedule from './ViewPreceptorSchedule.jsx';
import AdminResourceCenter from "./AdminResourceCenter.jsx";
import ChangePassword from "./ChangePassword";
import SpecialityInstitutionManager from "./SpecialityInstitutionManager.jsx";
import ManageCurriculums from "./ManageCurriculums.jsx";
import ManageRanks from "./ManageRanks.jsx";
import FormBuilderPage from "./FormBuilderPage.jsx";
import AdminPreceptorAvailabilityManager from "./AdminPreceptorAvailabilityManager.jsx";
import PerceptorAvailabilityForm from "./PerceptorAvailabilityForm.jsx";
import MakeAnnouncement from "./MakeAnnouncement.jsx";
import LoadingSpinner from './LoadingSpinner';
import FailurePage from './FailurePage.jsx';
import AllEvaluationsPage from "./AllEvaluationsPage.jsx";
import MyEvaluationsPage from "./MyEvaluationsPage.jsx";
import StudentAttendancePage from "./StudentAttendancePage.jsx";
import MyOrganizationPage from "./MyOrganizationPage.jsx";

// Views that already render their own full-width page shell — either the modern
// `mc-*` / universal-white-container layout used by Manage Curricula, or a bespoke
// one. Wrapping them in a `content-box` would nest a white card inside a white
// card, so they render bare inside `page-container` instead.
const SELF_SHELLED_VIEWS = new Set([
    "change-password",
    "my-account",
    "config",
    "manage-ranks",
    "manage-curricula",
    "create-user",
    "admin-resource-center",
    "manage-users",
    "import-students",
    "import-preceptors",
    "import-admins",
    "import-users",
    "preceptor-schedule",
    "choices",
    "dashboard",
    "form-builder",
    "speciality-institution-manager",
    "make-announcement",
    "preceptor-availability",
    "my-organization",
    "my-evaluations",
    "all-evaluations",
    "evaluation-center",
    "student-attendance",
]);

const AdminDashboard = () => {
    const [adminData, setAdminData] = useState(null);
    const [error, setError] = useState("");
    const [currentRotation, setCurrentRotation] = useState(null);
    const [loadKey, setLoadKey] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();
    const { view } = useParams();

    // Initialize currentView from URL parameter or default to "dashboard"
    const pathView = location.pathname.split('/').filter(Boolean)[1];
    const currentView = view || pathView || "dashboard";
    
    // Modern UI Styles
    const darkPageShellStyle = {
        background: 'transparent',
        minHeight: '100vh'
    };
    
    const modernContentBoxStyle = {
        border: '1px solid rgba(193, 212, 233, 0.38)',
        borderRadius: '22px',
        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(244, 248, 253, 0.98) 100%)',
        boxShadow: '0 20px 42px rgba(6, 20, 35, 0.24)'
    };

    // Update URL when view changes
    const handleViewChange = (newView) => {
        if (newView === "dashboard") {
            navigate("/AdminDashboard", { replace: true });
        } else {
            navigate(`/AdminDashboard/${newView}`, { replace: true });
        }
    };

    useEffect(() => {
        const fetchAdminData = async () => {
            try {
                const token = localStorage.getItem('access_token');
                if (!token) {
                    setError("Authentication failed. Please login again.");
                    navigate('/');
                    return;
                }

                const response = await axiosInstance.get("/auth/preceptor/dashboard");
                setAdminData(normalizeUser(response.data?.data || {}));

                try {
                    const studentsResponse = await axiosInstance.get(
                        "/crud/preceptor/schedule_of_current_rotation"
                    );
                    setCurrentRotation(studentsResponse.data);
                } catch (_e) {
                    // no current rotation — silently ignore
                }
            } catch (err) {
                console.error('❌ Dashboard API error:', err);
                if (err.response?.status === 403 && isImpersonating()) {
                    const restored = restoreAdminSession();
                    if (restored.success) {
                        window.location.href = restored.returnPath || '/AdminDashboard/manage-users';
                        return;
                    }
                }
                setError("Failed to fetch admin data. Please try again later.");
            }
        };

        fetchAdminData();
    }, [navigate, loadKey]);

    const QuickAction = ({ icon: ActionIcon, label, color, onClick }) => (
        <button 
            onClick={onClick}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem',
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                width: '100%',
                color: '#111827'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f8fafc';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.borderColor = color;
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#e5e7eb';
            }}
        >
            <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '10px', 
                background: color, 
                display: 'grid', 
                placeItems: 'center',
                fontSize: '1.1rem',
                color: 'white',
                boxShadow: `0 4px 12px ${color}33`
            }}>
                <ActionIcon />
            </div>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, textAlign: 'center', color: '#111827' }}>{label}</span>
        </button>
    );

    const renderDashboardContent = () => {
        return (
            <div className="modern-dashboard">
                <div className="dashboard-header">
                    <h1 className="dashboard-title" style={{ color: '#ffffff' }}>
                        Welcome, Dr. {adminData.firstname} {adminData.lastname}
                    </h1>
                    <p className="dashboard-subtitle" style={{ color: 'rgba(255, 255, 255, 0.92)' }}>
                        Administrator Control Panel • {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </p>
                </div>

                <div className="dashboard-grid">
                    <div className="dashboard-card dashboard-card--dark">
                        <div className="card-header">
                            <FaUser style={{ color: '#7dd3fc' }} />
                            <h2 className="card-title">Administrator Profile</h2>
                        </div>
                        <div className="card-content">
                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">ID Number</span>
                                    <span className="info-value">{adminData._id}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Email Address</span>
                                    <span className="info-value">{adminData?.email}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Current Role</span>
                                    <div className="info-value info-value-badges" style={{ justifyContent: 'center', width: '100%' }}>
                                        <span className="role-badge" style={{ background: '#1e3a8a' }}>Administrator</span>
                                        <span className="role-badge" style={{ background: '#065f46' }}>Preceptor</span>
                                    </div>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Department</span>
                                    <div className="info-value info-value-stack">
                                        <span className="info-value-main">
                                            {(Array.isArray(adminData.specialityNames) && adminData.specialityNames.length > 0)
                                                ? adminData.specialityNames.join(', ')
                                                : (adminData.specialityName || "Not Set")}
                                        </span>
                                        <span className="info-value-sub">
                                            {adminData.subSpeciality ? `(${adminData.subSpeciality})` : ""}
                                        </span>
                                    </div>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Institution</span>
                                    <div className="info-value info-value-stack">
                                        <span className="info-value-main">
                                            {(Array.isArray(adminData.institutionNames) && adminData.institutionNames.length > 0)
                                                ? adminData.institutionNames.join(', ')
                                                : (adminData.institutionName || "Not Set")}
                                        </span>
                                        <span className="info-value-sub">
                                            {adminData.subInstitution ? `(${adminData.subInstitution})` : ""}
                                        </span>
                                    </div>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Work Address</span>
                                    <span className="info-value" style={{ fontSize: '0.85rem' }}>{adminData.address || "Not Provided"}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="dashboard-card dashboard-card--dark">
                        <div className="card-header">
                            <FaClipboardCheck style={{ color: '#7dd3fc' }} />
                            <h2 className="card-title">Rotation Overview</h2>
                        </div>
                        <div className="card-content">
                            {currentRotation && (currentRotation.rotation_start || currentRotation.rotationStartDate) ? (
                                <>
                                    <div className="rotation-period" style={{ marginBottom: '1.5rem', background: '#f8fafc', borderLeft: '4px solid #10b981' }}>
                                        <span className="period-label" style={{ color: '#64748b' }}>Current Timeline</span>
                                        <span className="period-dates" style={{ color: '#111827' }}>
                                            {formatMongoDate(currentRotation.rotationStartDate || currentRotation.rotation_start)} 
                                            <span style={{ margin: '0 0.5rem', opacity: 0.5 }}>→</span>
                                            {formatMongoDate(currentRotation.rotationEndDate || currentRotation.rotation_end)}
                                        </span>
                                    </div>
                                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <h4 style={{ margin: 0, fontSize: '0.8rem', color: '#111827', fontWeight: 700, textTransform: 'uppercase' }}>Assigned Students</h4>
                                        <span className="info-pill" style={{ background: '#10b981' }}>{currentRotation.students?.length || 0} Total</span>
                                    </div>
                                    {currentRotation.students?.length > 0 ? (
                                        <div className="students-grid" style={{ display: 'grid', gap: '0.5rem', maxHeight: '200px', overflowY: 'auto', paddingRight: '0.5rem' }}>
                                            {currentRotation.students.map((student) => (
                                                <div key={student.id} style={{ 
                                                    background: '#f8fafc', 
                                                    borderRadius: '10px', 
                                                    padding: '0.75rem', 
                                                    border: '1px solid #e5e7eb',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center'
                                                }}>
                                                    <div>
                                                        <div style={{ fontWeight: 700, color: '#111827', fontSize: '0.85rem' }}>{student.name}</div>
                                                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{student.email}</div>
                                                    </div>
                                                    <FaGraduationCap style={{ color: '#4a90e2', opacity: 0.8 }} />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="no-data-message" style={{ background: '#f8fafc' }}>
                                            <div className="no-data-icon">👥</div>
                                            <p style={{ color: '#374151' }}>No active assignments</p>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="no-data-message" style={{ padding: '2rem 0', background: '#f8fafc' }}>
                                    <div className="no-data-icon" style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📅</div>
                                    <p style={{ margin: 0, fontWeight: 600, color: '#111827' }}>No active rotation cycle</p>
                                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', color: '#64748b' }}>Check configurations for upcoming periods</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '2rem', alignItems: 'stretch', marginBottom: '2rem' }}>
                    <div className="dashboard-card dashboard-card--dark" style={{ height: '100%' }}>
                        <div className="card-header">
                            <FaCog style={{ color: '#7dd3fc' }} />
                            <h2 className="card-title">Quick Actions</h2>
                        </div>
                        <div className="card-content" style={{ padding: '1rem' }}>
                            <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(2, 1fr)', 
                                gap: '0.75rem' 
                            }}>
                                <QuickAction 
                                    icon={FaUsers} 
                                    label="User Directory" 
                                    color="#3b82f6" 
                                    onClick={() => handleViewChange('manage-users')} 
                                />
                                <QuickAction 
                                    icon={FaCalendarAlt} 
                                    label="Rotation Schedules" 
                                    color="#10b981" 
                                    onClick={() => handleViewChange('schedule')} 
                                />
                                <QuickAction 
                                    icon={FaBullhorn} 
                                    label="Announcements" 
                                    color="#f59e0b" 
                                    onClick={() => handleViewChange('make-announcement')} 
                                />
                                <QuickAction 
                                    icon={FaFileAlt} 
                                    label="Evaluation Hub" 
                                    color="#8b5cf6" 
                                    onClick={() => handleViewChange('evaluation-center')} 
                                />
                                <QuickAction 
                                    icon={FaKey} 
                                    label="Security" 
                                    color="#ef4444" 
                                    onClick={() => handleViewChange('change-password')} 
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ minWidth: 0, height: '100%', display: 'flex', width: '100%' }}>
                        <DueEvents 
                            userId={adminData._id} 
                            userRole="admin" 
                        />
                    </div>
                </div>
            </div>
        );
    };

    const renderContent = () => {
        switch (currentView) {
            case "schedule-student":
                return <ScheduleByStudentPage />;
            case "schedule-preceptor":
                return <ScheduleByPreceptorPage />;
            case "schedule-speciality":
                return <ScheduleBySpecialityPage />;
            case "schedule-institution":
                return <ScheduleByInstitutionPage />;
            case "schedule-rotation":
                return <ScheduleByRotationPage />;
            case "schedule-reporting":
                return <ScheduleReportingPage />;
            case "preceptor-schedule":
                return <ViewPreceptorSchedule />;
            case "create-user":
                return <CreateUserForm />;
            case "choices":
                return <PerceptorAvailabilityForm />;
            case "schedule":
                return adminData.isAdmin ? 
                    <ScheduleByStudentPage /> : 
                    <ViewPreceptorSchedule />;
            case "forms":
                return <PreceptorEvaluationForm />;
            case "evaluation-center":
                return <AllEvaluationsPage />;
            case "all-evaluations":
                return <AllEvaluationsPage />;
            case "my-evaluations":
                return <MyEvaluationsPage userLabel="My Evaluations" />;
            case "student-attendance":
                return <StudentAttendancePage adminMode={true} />;
            case "my-organization":
                return <MyOrganizationPage canEdit={true} />;
            case "config":
                return <ConfigurationForm />;
            case "constraints":
                return <ConstraintsForm />;
            case "manage-users":
                return <ManageUsers />;
            case "my-account":
                return <ModifyUser userId={adminData?._id} userType="preceptor-admin" isAdmin={true} />;
            case "import-students":
                return <ImportUsers initialMode="students" />;
            case "import-preceptors":
                return <ImportUsers initialMode="preceptors" />;
            case "import-admins":
                return <ImportUsers initialMode="admins" />;
            case "import-users":
                return <ImportUsers />;
            case "export-students":
            case "export-preceptors":
            case "export-admins":
                return <ManageUsers />;
            case "admin-resource-center":
                return <AdminResourceCenter />;
            case "speciality-institution-manager":
                return <SpecialityInstitutionManager />;
            case "manage-ranks":
                return <ManageRanks />;
            case "manage-curricula":
                return <ManageCurriculums />;
            case "form-builder":
                return <FormBuilderPage />;
            case "preceptor-availability":
                return <AdminPreceptorAvailabilityManager />;
            case "make-announcement":
                return <MakeAnnouncement />;
            case "dashboard":
            default:
                return renderDashboardContent();
            case "change-password":
                return <ChangePassword />;
        }
    };

    if (error) {
        return (
            <FailurePage
                title="Oops, admin dashboard failed"
                message={error}
                actionLabel={error.includes('login') ? 'Go to login' : 'Retry'}
                onAction={() => {
                    if (error.includes('login')) {
                        navigate('/');
                        return;
                    }
                    setError('');
                    setCurrentRotation(null);
                    setAdminData(null);
                    setLoadKey((key) => key + 1);
                }}
            />
        );
    }

    if (!adminData) {
        return (
            <div className="page-container" style={darkPageShellStyle}>
                <div className="content-box" style={modernContentBoxStyle}>
                    <LoadingSpinner text="Loading admin dashboard..." variant="page" />
                </div>
            </div>
        );
    }

    return (
        <div className="page-container" style={darkPageShellStyle}>
            <CollapsibleNavbar 
                setCurrentView={handleViewChange} 
                userType="preceptor" 
                isStandaloneAdmin={false} 
                isAdmin={true}
                onFormBuilderClick={() => handleViewChange("form-builder")}
            />
            {SELF_SHELLED_VIEWS.has(currentView) ? (
                renderContent()
            ) : (
                <div className="content-box" style={modernContentBoxStyle}>
                    {renderContent()}
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
